import { uniq } from 'lodash-es';

import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subject, of } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { MwUserService, MwAuthService } from '@core/auth';
import { ProfileMenuItem } from '@core/config';
import { MwAppConfigService, MwIconLoaderService } from '@core/services';

@Injectable()
export class MwProfileMenuDataService implements OnDestroy {
  static nextId = 0;
  readonly _id = MwProfileMenuDataService.nextId++;

  get dataChanged$(): Observable<void> {
    return this._dataChanged$.asObservable();
  }

  companyName: string;
  userFullName: string;
  nameInitials: string;
  profileMenuItems: ProfileMenuItem[] = [];

  private isProfileMenuItemVisible = true;
  private readonly unsubscribeAll$ = new Subject<void>();
  private readonly _dataChanged$ = new Subject<void>();

  constructor(
    private userService: MwUserService,
    private authService: MwAuthService,
    private configService: MwAppConfigService,
    private iconLoaderService: MwIconLoaderService
  ) {
    this.subscribeToCompanyName();
    this.subscribeToUserFullName();
    this.subscribeToProfileMenuItems();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
  private subscribeToCompanyName(): void {
    this.authService.activeCompany$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(
        (company) => (this.companyName = company?.companyName ?? 'Not set')
      );
  }

  private subscribeToUserFullName(): void {
    this.userService.userProfile$
      .pipe(
        takeUntil(this.unsubscribeAll$),
        switchMap((profile) => {
          if (profile) {
            return of(profile);
          } else {
            return this.authService.userData$.pipe(
              map((data) => {
                if (data) {
                  return {
                    firstName: data.name,
                    lastName: ' ',
                    userId: 0,
                  };
                } else {
                  return null;
                }
              })
            );
          }
        })
      )
      .subscribe((profile) => {
        if (profile) {
          this.nameInitials = profile.firstName[0] + profile.lastName[0];
          this.userFullName = profile.firstName + ' ' + profile.lastName;
        } else {
          this.nameInitials = 'NS';
          this.userFullName = 'Not set';
        }

        this.isProfileMenuItemVisible = !!profile?.userId;
        this.profileMenuItems = this.updateProfileMenuItemsVisibility([
          ...this.profileMenuItems,
        ]);
      });
  }

  private subscribeToProfileMenuItems(): void {
    this.configService
      .getConfig()
      .pipe(
        takeUntil(this.unsubscribeAll$),
        map((config) => config.profileMenuItems),
        filter((profileMenuItems) => !!profileMenuItems),
        map((profileMenuItems) =>
          profileMenuItems.sort((a, b) => (a.orderRank > b.orderRank ? 1 : -1))
        ),
        tap((menuItems) => this.loadIcons(menuItems))
      )
      .subscribe((menuItems) => {
        this.profileMenuItems = this.updateProfileMenuItemsVisibility([
          ...menuItems,
        ]);
        this.triggerDataChangeEvent();
      });
  }

  private loadIcons(menuItems: ProfileMenuItem[]): void {
    const svgIcons = menuItems
      .map((t) => t.svgIcon)
      .filter((svgIcon) => !!svgIcon);

    const uniqueSvgIcons = uniq(svgIcons);
    this.iconLoaderService.load(uniqueSvgIcons);
  }

  private updateProfileMenuItemsVisibility(
    items: ProfileMenuItem[]
  ): ProfileMenuItem[] {
    if (this.isProfileMenuItemVisible) {
      return items;
    }

    return items.filter(
      (item) => item.title !== 'CORE.PROFILE.MENU.my_profile'
    );
  }

  private triggerDataChangeEvent(): void {
    this._dataChanged$.next();
  }
}
